import { FILTER_OPTION_TYPE } from "Common/Constants";
import type { IAutocompleteFilterOption, IDatePresetRangeFilterValue, IFilterOption } from "Common/Interfaces";
import type dayjs from "dayjs";
import React, { useEffect } from "react";

interface IUseEnhancedFilter {
  currentFilterState: IFilterOption[];
}

export const useEnhancedFilter = (props: IUseEnhancedFilter) => {
  const { currentFilterState } = props;

  // Filter Menu State
  const [filterMenu, setFilterMenu] = React.useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
  }>({
    open: false,
    anchorEl: null,
  });

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenu({
      open: !filterMenu.open,
      anchorEl: e.currentTarget,
    });
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (filterMenu.anchorEl?.contains(event.target as HTMLElement)) {
      return;
    }
    setFilterMenu({
      open: false,
      anchorEl: null,
    });
  };

  // Filter State
  const [tempFilterState, setTempFilterState] = React.useState<IFilterOption[]>(currentFilterState);
  const [selectedFilterOption, setSelectedFilterOption] = React.useState<IFilterOption | null>(null);

  useEffect(() => {
    setTempFilterState(currentFilterState);
    setSelectedFilterOption(currentFilterState[0]);
  }, [currentFilterState, filterMenu.open]);

  // Handle Search
  const [searchText, setSearchText] = React.useState<{ [key: number | string]: string }>({});

  const handleSearchChange = (filterOptionId: number | string, search: string) => {
    setSearchText((prev) => ({
      ...prev,
      [filterOptionId]: search,
    }));
  };

  useEffect(() => {
    if (!filterMenu.open) {
      setSearchText({});
    }
  }, [filterMenu.open]);

  const getFilterValuesBasedOnSearch = (filterOptionId: number | string) => {
    const search = searchText[filterOptionId]?.toLowerCase().trim() || "";

    const filterOption = tempFilterState.find((option) => option.id === filterOptionId);

    if (filterOption && filterOption.type === FILTER_OPTION_TYPE.CHECKBOX && filterOption.checkboxValues) {
      const filteredCheckboxValues = filterOption.checkboxValues.filter((filterValue) =>
        filterValue.label.toLowerCase().includes(search)
      );

      return filteredCheckboxValues;
    }

    // Return all values if no search text or not a checkbox type
    return filterOption?.checkboxValues || [];
  };

  const handleSetSelectedFilterOption = (filterOption: IFilterOption) => {
    setSelectedFilterOption(filterOption);
  };

  // Handle Checkbox
  const handleCheckboxChange = (filterOptionId: number | string, value: number | string | boolean) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (filterOption.id === filterOptionId) {
        return {
          ...filterOption,
          checkboxValues: filterOption.checkboxValues?.map((filterValue) => {
            if (filterValue.value.toString() === value.toString()) {
              return {
                ...filterValue,
                isChecked: !filterValue.isChecked,
              };
            }
            return filterValue;
          }),
        };
      }
      return filterOption;
    });

    setTempFilterState(updatedFilterState);
  };

  //Handle Autocomplete selection
  const handleAutocompleteSelect = (filterOptionId: number | string, value: IAutocompleteFilterOption | null) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (filterOption.id === filterOptionId && filterOption.autoCompleteValues) {
        return {
          ...filterOption,
          autoCompleteValues: {
            ...filterOption.autoCompleteValues,
            value: value,
          },
        };
      }
      return filterOption;
    });
    setTempFilterState(updatedFilterState);
  };

  // Handle Multiple Autocomplete selection
  const handleMultipleAutocompleteSelect = (filterOptionId: number | string, value: IAutocompleteFilterOption[]) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (filterOption.id === filterOptionId && filterOption.multipleAutocompleteValues) {
        return {
          ...filterOption,
          multipleAutocompleteValues: {
            ...filterOption.multipleAutocompleteValues,
            value: value,
          },
        };
      }
      return filterOption;
    });
    setTempFilterState(updatedFilterState);
  };

  //Handle date range selection
  const [hasDateRangeError, setHasDateRangeError] = React.useState<boolean>(false);
  const handleDateRangeSelect = (
    filterOptionId: number | string,
    date: dayjs.Dayjs | null,
    position: "startDate" | "endDate",
    hasValidationError: boolean
  ) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (filterOption.id === filterOptionId && filterOption.dateRangeValues) {
        return {
          ...filterOption,
          dateRangeValues: {
            ...filterOption.dateRangeValues,
            [position]: date,
          },
        };
      }
      return filterOption;
    });
    setHasDateRangeError(hasValidationError);
    setTempFilterState(updatedFilterState);
  };

  // Handle Autocomplete of Autocomplete with checkbox
  const handleAutocompleteWithCheckboxSelect = (
    filterOptionId: number | string,
    value: IAutocompleteFilterOption | null
  ) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (
        filterOption.id === filterOptionId &&
        filterOption.autoCompleteWithCheckboxValues &&
        filterOption.autoCompleteWithCheckboxValues.checkboxValue
      ) {
        return {
          ...filterOption,
          autoCompleteWithCheckboxValues: {
            ...filterOption.autoCompleteWithCheckboxValues,
            value: value,
            checkboxValue: {
              ...filterOption.autoCompleteWithCheckboxValues.checkboxValue,
              isChecked: false,
            },
          },
        };
      }
      return filterOption;
    });
    setTempFilterState(updatedFilterState);
  };

  // Handle Checkbox of Autocomplete with checkbox
  const handleCheckboxChangeForAutocompleteWithCheckbox = (filterOptionId: number | string, isChecked: boolean) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (
        filterOption.id === filterOptionId &&
        filterOption.autoCompleteWithCheckboxValues &&
        filterOption.autoCompleteWithCheckboxValues.checkboxValue
      ) {
        return {
          ...filterOption,
          autoCompleteWithCheckboxValues: {
            ...filterOption.autoCompleteWithCheckboxValues,
            value: null,
            checkboxValue: {
              ...filterOption.autoCompleteWithCheckboxValues.checkboxValue,
              isChecked: isChecked,
            },
          },
        };
      }
      return filterOption;
    });
    setTempFilterState(updatedFilterState);
  };

  //Handle date preset range update
  const handleDatePresetRangeUpdate = (filterOptionId: number | string, updates: Partial<IDatePresetRangeFilterValue>) => {
    const updatedFilterState = tempFilterState.map((filterOption) => {
      if (filterOption.id === filterOptionId && filterOption.datePresetRangeValues) {
        return {
          ...filterOption,
          datePresetRangeValues: { ...filterOption.datePresetRangeValues, ...updates },
        };
      }
      return filterOption;
    });
    setTempFilterState(updatedFilterState);
  };

  return {
    filterMenu,
    tempFilterState,
    searchText,
    selectedFilterOption,
    hasDateRangeError,
    setTempFilterState,
    handleToggle,
    handleClose,
    handleSearchChange,
    getFilterValuesBasedOnSearch,
    handleCheckboxChange,
    handleSetSelectedFilterOption,
    handleAutocompleteSelect,
    handleDateRangeSelect,
    handleMultipleAutocompleteSelect,
    handleAutocompleteWithCheckboxSelect,
    handleCheckboxChangeForAutocompleteWithCheckbox,
    handleDatePresetRangeUpdate,
  };
};
